if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.form.addEventListener('input', this.toggleAcceleratedCheckout.bind(this));
        this.toggleAcceleratedCheckout();
      }

      getCustomTextInputs() {
        const scoped = this.form?.id
          ? Array.from(
              document.querySelectorAll(
                `input[data-custom-text-fee][form="${this.form.id}"], #${CSS.escape(this.form.id)} input[data-custom-text-fee]`
              )
            )
          : [];
        if (scoped.length) return scoped;
        return Array.from(document.querySelectorAll('input[data-custom-text-fee]'));
      }

      getCustomTextFees() {
        return this.getCustomTextInputs()
          .map((input) => ({
            text: (input.value || '').trim(),
            variantId: input.dataset.feeVariantId || window.customTextFeeVariantId,
            propertyName: input.dataset.propertyName || 'Custom text',
          }))
          .filter((fee) => fee.text && fee.variantId);
      }

      toggleAcceleratedCheckout() {
        const payButton = this.querySelector('.shopify-payment-button');
        if (!payButton) return;
        payButton.hidden = this.getCustomTextFees().length > 0;
      }

      setEmptyCustomTextInputsDisabled(disabled) {
        this.getCustomTextInputs().forEach((input) => {
          if (disabled) {
            input.disabled = !(input.value || '').trim();
          } else {
            input.disabled = false;
          }
        });
      }

      buildItemsWithCustomTextFees(formData) {
        const fees = this.getCustomTextFees();
        if (!fees.length) return null;

        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[([^\]]+)\]$/);
          if (match && String(value).trim() !== '') {
            properties[match[1]] = value;
          }
        }

        const feeGroupId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        properties._custom_text_fee_id = feeGroupId;

        const quantity = Number(formData.get('quantity')) || 1;
        const mainItem = {
          id: Number(formData.get('id')),
          quantity,
          properties,
        };

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) mainItem.selling_plan = Number(sellingPlan);

        const items = [mainItem];
        fees.forEach((fee) => {
          if (!fee.variantId || Number(fee.variantId) === mainItem.id) return;
          items.push({
            id: Number(fee.variantId),
            quantity: 1,
            properties: {
              _custom_text_fee: 'true',
              _custom_text_fee_id: feeGroupId,
              [fee.propertyName]: fee.text,
            },
          });
        });

        return items.length > 1 ? items : null;
      }

      addCustomTextFees(fees, feeGroupId, formData) {
        const feeVariantId = Number(fees[0]?.variantId);
        if (!feeVariantId || feeVariantId === Number(formData.get('id'))) {
          return Promise.resolve(null);
        }

        return fetch(`${routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cart) => {
            const feeAlreadyInCart = cart.items.some(
              (item) =>
                item.properties?._custom_text_fee === 'true' || Number(item.variant_id) === feeVariantId
            );
            if (feeAlreadyInCart) return null;

            const payload = {
              items: [
                {
                  id: feeVariantId,
                  quantity: 1,
                  properties: {
                    _custom_text_fee: 'true',
                    _custom_text_fee_id: feeGroupId,
                    [fees[0].propertyName]: fees[0].text,
                  },
                },
              ],
            };

            if (this.cart) {
              payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
              payload.sections_url = window.location.pathname;
            }

            const config = fetchConfig('javascript');
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            config.body = JSON.stringify(payload);

            return fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
          });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        this.setEmptyCustomTextInputsDisabled(true);
        const formData = new FormData(this.form);
        const customTextFees = this.getCustomTextFees();
        const feeGroupId = customTextFees.length
          ? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
          : null;

        if (feeGroupId) {
          formData.append('properties[_custom_text_fee_id]', feeGroupId);
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        delete config.headers['Content-Type'];
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (!customTextFees.length) return response;

            return this.addCustomTextFees(customTextFees, feeGroupId, formData).then((feeResponse) => {
              if (feeResponse?.status) {
                this.handleErrorMessage(feeResponse.description || 'Custom text fee could not be added.');
                this.error = true;
                return response;
              }
              return feeResponse || response;
            });
          })
          .then((response) => {
            if (!response || this.error && !response.items && !response.sections) return;
            if (this.error && response.status) return;

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.setEmptyCustomTextInputsDisabled(false);
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
